/**
 * Per-claim / selected-claims export control for the POD schedule (MOR-878).
 *
 * Mirrors the voidables `FlagExportMenu`: an Excel / Word / PDF popover that
 * exports one POD claim (the row action and the claim slide-over) or the rows
 * the practitioner has ticked (the bulk action), as a file-note style
 * adjudication record.
 *
 * It reads `listPodClaimsForExport` and filters to the requested claims
 * client-side. The query only mounts once the popover is opened, so putting
 * an instance on every table row costs nothing until it's used — and
 * convexQuery dedupes the subscription across every instance on the page.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { saveAs } from "file-saver";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/ui/primitives/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { cn } from "@/utils/misc";
import { exportToExcel, exportToWord, exportToPdf } from "@/utils/export";
import {
  buildPodClaimNoteExport,
  podClaimNoteFileName,
  type PodClaimExportRow,
} from "./buildPodClaimsExport";

type ExportFormat = "excel" | "word" | "pdf";

const FORMATS: Array<{
  key: ExportFormat;
  label: string;
  ext: string;
  icon: typeof FileText;
}> = [
  { key: "excel", label: "Excel", ext: "xlsx", icon: FileSpreadsheet },
  { key: "word", label: "Word", ext: "docx", icon: FileText },
  { key: "pdf", label: "PDF", ext: "pdf", icon: FileText },
];

export function PodClaimExportMenu(props: {
  jobId: Id<"jobs">;
  /** Claims to export, in the order they should read in the document. */
  claimIds: string[];
  /** "icon" is the compact row action; "button" is the labelled control. */
  variant?: "icon" | "button";
  /** Override the labelled variant's text (defaults to "Export"). */
  label?: string;
  className?: string;
}) {
  const { jobId, claimIds, variant = "button", label, className } = props;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data } = useQuery({
    ...convexQuery(api.jobs.recoveries.publicQueries.listPodClaimsForExport, {
      jobId,
    }),
    // Only subscribe once the practitioner actually opens the menu.
    enabled: open,
  });
  const allClaims = data as PodClaimExportRow[] | undefined;

  // Keep the caller's order (row order / selection order), not the query's.
  const claims = useMemo(() => {
    if (!allClaims) return undefined;
    const byId = new Map(allClaims.map((c) => [c.claimId, c]));
    return claimIds
      .map((id) => byId.get(id))
      .filter((c): c is PodClaimExportRow => c !== undefined);
  }, [allClaims, claimIds]);

  const handleExport = async ({
    key,
    ext,
  }: {
    key: ExportFormat;
    ext: string;
  }) => {
    if (!claims || claims.length === 0) return;
    setBusy(key);
    try {
      const content = buildPodClaimNoteExport(claims);
      const blob =
        key === "excel"
          ? exportToExcel(content)
          : key === "word"
            ? await exportToWord(content)
            : await exportToPdf(content);
      saveAs(blob, `${podClaimNoteFileName(claims)}.${ext}`);
      setOpen(false);
    } catch (err) {
      console.error(`POD claim export (${key}) failed:`, err);
      toast.error("Export failed — please try again");
    } finally {
      setBusy(null);
    }
  };

  const loading = open && claims === undefined;
  // The claim was deleted or merged between the table read and the export read.
  const missing = claims !== undefined && claims.length === 0;
  const count = claimIds.length;
  const title =
    count === 1 ? "Export this claim" : `Export ${count} selected claims`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {variant === "icon" ? (
          <Button
            variant="ghost"
            size="sm"
            className={cn("h-7 w-7 p-0", className)}
            title={title}
            aria-label={title}
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className={cn("h-7 gap-1 text-xs", className)}
            title={title}
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="h-3 w-3" />
            {label ?? "Export"}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-xs font-medium text-foreground">
          {count === 1 ? "Export claim" : `Export ${count} claims`}
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          {missing
            ? "This claim is no longer available to export."
            : "Claim detail, adjudication outcome, grounds, and line items."}
        </p>
        <div className="mt-3 grid grid-cols-3 gap-1.5">
          {FORMATS.map((f) => (
            <Button
              key={f.key}
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={loading || missing || busy !== null}
              onClick={() => void handleExport(f)}
            >
              {busy === f.key || (loading && busy === null) ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <f.icon className="h-3.5 w-3.5" />
              )}
              {f.label}
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
